import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import cn from 'classnames';

import { get, isEqual, map, set, size } from 'lodash-es';

import Coverage from './Blocks/Coverage';
import Data from './Blocks/Data';
import Formula from './Blocks/Formula';
import Package from './Blocks/Package';
import RowFooter from './Blocks/RowFooter';
import RowHead from './Blocks/RowHead';

import { checkIsEmpty } from '../../Utils/utils';
import { dataItemShape, extraDataItemShape, formulasItemShape, stringsItemShape } from './OfferBlock.shape';
import { dataOfferTransform, formulasTransform } from './OfferBlock.transform';

import './OfferBlock.scss';

/**
 * offer blocs
 * @param formulasRecommended
 * @param formulaSelectedProp
 * @param dataOfferProp
 * @param strings
 * @param eventEmitterSelectFormula
 * @returns {XML}
 * @constructor
 */
const OfferBlock = ({ mandatory, formulaSelectedProp, dataOfferProp, strings, eventEmitterSelectFormula }) => {
  const [formulaSelected, setFormulaSelected] = useState(formulaSelectedProp);
  //
  const [dataOffer, setDataOffer] = useState(dataOfferTransform(dataOfferProp));
  const [formulas] = useState(formulasTransform(dataOfferProp));
  const [formulasRecommended] = useState(get(dataOfferProp, 'formulaRecommended'));

  useEffect(
    () => {
      let isValid;
      switch (mandatory) {
        case true:
          isValid = !checkIsEmpty(formulaSelected);
          break;
        case false:
          isValid = true;
          break;
        default:
          break;
      }
      eventEmitterSelectFormula({
        isValid,
        answers: formulaSelected,
      });
    },
    //
    [formulaSelected]
  );

  /**
   * on Select Formula
   * @param formula
   */
  const onSelectFormula = formula => {
    if (isEqual(formula, formulaSelected)) {
      // check mandatory
      switch (mandatory) {
        case true:
          setFormulaSelected(formula);
          break;
        case false:
          setFormulaSelected('');
          break;
        default:
          break;
      }
    } else {
      setFormulaSelected(formula);
    }
  };

  /**
   * on Show Details
   * @param id
   * @param show
   */
  const onShowDetails = (id, show) => {
    const { data } = dataOffer;

    const newData = map(data, item => {
      if (item.id === id) {
        set(item, 'showDetails', !show);
      }
      return item;
    });
    setDataOffer({ ...dataOffer, data: newData });
  };

  /**
   * handle select formula
   * @param formula
   */
  const handleSelectFormula = formula => {
    onSelectFormula(formula);
  };

  /**
   * handle Show Details
   * @param id
   * @param show
   */
  const handleShowDetails = (id, show) => {
    onShowDetails(id, show);
  };

  return (
    <div className="offer-block-6d97ad81-714e-4ca2-816d-85b12a71f01a">
      <div className="flex-column">
        <RowHead title={get(dataOffer, 'extraData.title')} beneficiary={get(dataOffer, 'extraData.beneficiary')}>
          {map(formulas, formula => (
            <Formula
              key={get(formula, 'code')}
              code={get(formula, 'code')}
              value={get(formula, 'value')}
              formulaSelected={formulaSelected}
              formulasRecommended={formulasRecommended}
              strings={strings}
            />
          ))}
        </RowHead>

        {map(get(dataOffer, 'data'), dataItem => (
          <Data
            key={get(dataItem, 'id')}
            id={get(dataItem, 'id')}
            label={get(dataItem, 'label')}
            showDetails={get(dataItem, 'showDetails')}
            existCoverages={size(get(dataItem, 'packages[0].coverages')) > 0}
            type={get(dataItem, 'type')}
            handleShowDetails={handleShowDetails}
          >
            {map(get(dataItem, 'packages'), packageItem => (
              <Package
                key={get(packageItem, 'ref')}
                refProp={get(packageItem, 'ref')}
                value={get(packageItem, 'value')}
                showDetails={get(dataItem, 'showDetails')}
                formulaSelected={formulaSelected}
              >
                {map(get(packageItem, 'coverages'), (coveragesItem, index) => (
                  <Coverage
                    key={index}
                    showSubBorder={size(get(packageItem, 'coverages')) > index + 1}
                    name={get(coveragesItem, 'name')}
                    type={get(coveragesItem, 'type')}
                    value={get(coveragesItem, 'value')}
                  />
                ))}
              </Package>
            ))}
          </Data>
        ))}

        <RowFooter labelFooter={get(dataOffer, 'extraData.labelFooter')}>
          {map(formulas, formula => (
            <div key={get(formula, 'code')} className="flex-grid flex-grid-center-horizontal">
              <button
                className={cn('style-button-choice-formula padding-box text-capitalize', {
                  selected_button: get(formula, 'code') === formulaSelected,
                })}
                onClick={() => handleSelectFormula(get(formula, 'code'))}
                type="button"
              >
                {' '}
                {get(strings, 'labelButton')}{' '}
              </button>
            </div>
          ))}
        </RowFooter>
      </div>
    </div>
  );
};

OfferBlock.propTypes = {
  mandatory: PropTypes.bool.isRequired,
  formulaSelectedProp: PropTypes.string,
  dataOfferProp: PropTypes.shape({
    data: PropTypes.arrayOf(dataItemShape),
    formulas: PropTypes.arrayOf(formulasItemShape).isRequired,
    formulaRecommended: PropTypes.string.isRequired,
    extraData: PropTypes.shape(extraDataItemShape),
  }).isRequired,
  strings: PropTypes.shape(stringsItemShape).isRequired,
  eventEmitterSelectFormula: PropTypes.func.isRequired,
};

OfferBlock.defaultProps = {
  formulaSelectedProp: '',
};

export default OfferBlock;
